import React, { createContext, useContext, useMemo, useEffect } from "react";
import * as projectService from "../services/projectService";
import * as projectObjectiveService from "../services/projectObjectiveService";
import * as projectQualStepService from "../services/projectQualStepService";
import * as projectPageService from "../services/projectPageService";
import * as projectPageComponentService from "../services/projectPageComponentService";
import {
  sfGet,
  sfPost,
  sfPut,
  sfPatch,
  sfDelete,
  setAuthTokenProvider,
  setUnauthorizedHandler,
  searchAccountByName,
  searchContributorByName,
  searchUsersByEmail,
} from "../services/salesforceApi";
import * as pageUtilsService from "../services/pageUtilsService";
import { env } from "../config/env";
import { useOptionalAuth } from "../auth/AuthContext";
import { isPlatformSessionMode } from "../auth/mode";
import { useSessionError } from "../components/SessionErrorHandler";
import { isSessionError } from "../utils/sessionErrorUtils";

const ServiceContext = createContext(null);

function buildDefaultServices() {
  // Project repository-like surface
  const project = {
    fetchProject: projectService.fetchProject,
    fetchAllProjects: projectService.fetchAllProjects,
    searchProjects: projectService.searchProjects,
    createProject: projectService.createProject,
    updateProject: projectService.updateProject,
    patchProject: projectService.patchProject,
  };

  // Project Objective surface
  const projectObjective = {
    fetchProjectObjective: projectObjectiveService.fetchProjectObjective,
    fetchProjectObjectivesByProjectId:
      projectObjectiveService.fetchProjectObjectivesByProjectId,
    fetchAllProjectObjectives:
      projectObjectiveService.fetchAllProjectObjectives,
    searchProjectObjectives: projectObjectiveService.searchProjectObjectives,
    patchProjectObjective: projectObjectiveService.patchProjectObjective,
  };

  // Qualification Steps surface
  const projectQualStep = {
    fetchQualStep: projectQualStepService.fetchQualStep,
    fetchQualStepsByProjectId: projectQualStepService.fetchQualStepsByProjectId,
    fetchQualStepsByProjectObjectiveId:
      projectQualStepService.fetchQualStepsByProjectObjectiveId,
    fetchAllQualSteps: projectQualStepService.fetchAllQualSteps,
    searchQualSteps: projectQualStepService.searchQualSteps,
    patchQualStep: projectQualStepService.patchQualStep,
  };

  // Project Pages surface
  const projectPage = {
    fetchProjectPage: projectPageService.fetchProjectPage,
    fetchProjectPagesByProjectId:
      projectPageService.fetchProjectPagesByProjectId,
    fetchProjectPagesByProjectObjectiveId:
      projectPageService.fetchProjectPagesByProjectObjectiveId,
    fetchAllProjectPages: projectPageService.fetchAllProjectPages,
    searchProjectPages: projectPageService.searchProjectPages,
    patchProjectPage: projectPageService.patchProjectPage,
  };

  // Project Page Components surface
  const projectPageComponent = {
    fetchProjectPageComponent:
      projectPageComponentService.fetchProjectPageComponent,
    fetchProjectPageComponentsByProjectPageId:
      projectPageComponentService.fetchProjectPageComponentsByProjectPageId,
    fetchAllProjectPageComponents:
      projectPageComponentService.fetchAllProjectPageComponents,
    searchProjectPageComponents:
      projectPageComponentService.searchProjectPageComponents,
    patchProjectPageComponent:
      projectPageComponentService.patchProjectPageComponent,
  };

  // Account surface
  const account = {
    searchAccounts: searchAccountByName,
  };

  // User surface
  const user = {
    searchUsers: searchUsersByEmail,
  };

  const contributor = {
    searchContributors: searchContributorByName,
  };

  // Page Utils surface
  const pageUtils = {
    fetchProjectPagePreview: pageUtilsService.fetchProjectPagePreview,
  };
  // Low-level HTTP
  const http = { sfGet, sfPost, sfPut, sfPatch, sfDelete };

  // Configuration accessors
  const config = {
    baseUrl: env.sfBaseUrl,
    apexPrefix: env.sfApexPrefix,
    withCredentials: env.sfWithCredentials,
  };

  // Auth hooks
  const auth = { setAuthTokenProvider, setUnauthorizedHandler };

  return {
    project,
    projectObjective,
    projectQualStep,
    projectPage,
    projectPageComponent,
    account,
    user,
    contributor,
    pageUtils,
    http,
    config,
    auth,
  };
}

export const ServiceProvider = ({ services, children }) => {
  const { handleSessionError } = useSessionError();
  const inPlatform = isPlatformSessionMode();
  const auth = useOptionalAuth();

  // Wire Salesforce API token provider to current access token when in OAuth mode
  useEffect(() => {
    if (inPlatform || !auth) {
      try {
        setAuthTokenProvider(() => undefined);
      } catch (_) {}
      return;
    }
    try {
      const token = auth?.accessToken;
      setAuthTokenProvider(() => token);
    } catch (_) {}
  }, [inPlatform, auth, auth?.accessToken]);

  const value = useMemo(() => {
    const defaults = buildDefaultServices();

    // Wrap HTTP methods to handle session errors
    const wrappedHttp = {
      sfGet: async (...args) => {
        try {
          return await sfGet(...args);
        } catch (error) {
          if (isSessionError(error)) {
            handleSessionError(error);
            // Don't re-throw session errors to prevent uncaught promise rejections
            return null;
          }
          throw error;
        }
      },
      sfPost: async (...args) => {
        try {
          return await sfPost(...args);
        } catch (error) {
          if (isSessionError(error)) {
            handleSessionError(error);
            return null;
          }
          throw error;
        }
      },
      sfPut: async (...args) => {
        try {
          return await sfPut(...args);
        } catch (error) {
          if (isSessionError(error)) {
            handleSessionError(error);
            return null;
          }
          throw error;
        }
      },
      sfPatch: async (...args) => {
        try {
          return await sfPatch(...args);
        } catch (error) {
          if (isSessionError(error)) {
            handleSessionError(error);
            return null;
          }
          throw error;
        }
      },
      sfDelete: async (...args) => {
        try {
          return await sfDelete(...args);
        } catch (error) {
          if (isSessionError(error)) {
            handleSessionError(error);
            return null;
          }
          throw error;
        }
      },
    };

    return {
      ...defaults,
      http: wrappedHttp,
      ...(services || {}),
    };
  }, [services, handleSessionError]);

  return (
    <ServiceContext.Provider value={value}>{children}</ServiceContext.Provider>
  );
};

export function useServices() {
  const ctx = useContext(ServiceContext);
  if (!ctx)
    throw new Error("useServices must be used within a ServiceProvider");
  return ctx;
}
